/**
 * @file api-product-kinds.ts
 * @description Клиент справочника «Вид товара» `/admin/catalog/product-kinds`
 * (phase19.22, inventory.md W22).
 *
 * Зеркало `apps/api/src/catalog/product-kinds/{dto,product-kinds.service}.ts`
 * и docs/api/catalog-product-kinds.md. Список отдаёт `meta.productsWithoutKind`
 * — поэтому через `apiFetchWithMeta`, остальные ручки — обычный `apiFetch`
 * (ошибки летят `ApiError` с `code`/`details`).
 *
 * Срок по умолчанию — в ДНЯХ (W20/W22); месяцы — только подпись в UI.
 */

import { apiFetch, apiFetchWithMeta } from "./api-client";
import type { LabelTemplate } from "./api-labels";

/**
 * Шаблон складской этикетки (W17-5b): 70×37 / 52.5×29.7 / 20×20 мм. Источник
 * типа один — клиент этикеток склада; каталог его переэкспортирует.
 */
export type { LabelTemplate };

/** Регламент маркировки потребительской упаковки. */
/** Регламент маркировки; `OTHER` — «Другой (указать)», название в `labelingRegulationNote`. */
export type LabelingRegulation = "TR_TS_022" | "TR_TS_009" | "NONE" | "OTHER";

/** Вид товара (ответ сервера `ProductKindDto`). */
export interface ProductKind {
  id: string;
  shopId: string;
  name: string;
  /** Машинный ключ — генерирует сервер, не редактируется. */
  slug: string;
  requiresExpiry: boolean;
  /** Срок годности по умолчанию, дней (1..3660); null — не задан. */
  defaultShelfLifeDays: number | null;
  returnToStockAllowed: boolean;
  labelTemplate: LabelTemplate;
  labelingRegulation: LabelingRegulation;
  /** Название регламента при `OTHER` (≤120, справочно); иначе null. */
  labelingRegulationNote: string | null;
  /** Ставка НДС по умолчанию, целые %; null — проставляется вручную. */
  defaultVatRate: number | null;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  /** Товаров с этим ДЕЙСТВУЮЩИМ видом (свой + от категории), без архивных. */
  productsCount: number;
  /** Товаров со своим видом = этот. */
  ownProductsCount: number;
  /** Категорий с видом по умолчанию = этот. */
  categoriesCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Тело создания вида; все поля кроме `name` — со значениями сервера по умолчанию. */
export interface ProductKindInput {
  name: string;
  requiresExpiry?: boolean;
  defaultShelfLifeDays?: number | null;
  returnToStockAllowed?: boolean;
  labelTemplate?: LabelTemplate;
  labelingRegulation?: LabelingRegulation;
  /** Обязательно при `OTHER` (422 `labeling_regulation_note_required`), ≤120. */
  labelingRegulationNote?: string | null;
  defaultVatRate?: number | null;
  description?: string | null;
  sortOrder?: number;
}

/** Действующий вид товара/фасовки (`effectiveKind` в ответах каталога). */
export interface EffectiveKind {
  id: string;
  name: string;
  requiresExpiry: boolean;
  labelTemplate: LabelTemplate;
  defaultShelfLifeDays: number | null;
  source: "product" | "category";
  /** Категория-источник при `source = "category"`. */
  categoryId: string | null;
}

/** Строка «Проставить вид по категориям». */
export interface AssignPreviewCategory {
  id: string;
  displayName: string;
  parentId: string | null;
  /** «Корень / … / Категория». */
  path: string;
  /** Товаров прямо в категории без вида (не архивных). */
  productsCount: number;
  /** Предложение сервера по названию категории/предков. */
  suggestedKindId: string | null;
}

export interface AssignPreviewResult {
  categories: AssignPreviewCategory[];
  /** Всего товаров без действующего вида. */
  productsWithoutKind: number;
  /** Из них в системных категориях — там вида не бывает. */
  productsInSystemCategories: number;
}

export interface AssignResult {
  updatedCategories: number;
  productsAffected: number;
  lotsRecalculated: number;
}

/** `details` ошибки 409 `product_kind_in_use`. */
export interface ProductKindInUseDetails {
  productKindId: string;
  categoriesCount: number;
  productsCount: number;
}

const BASE = "/admin/catalog/product-kinds";

export const productKindsApi = {
  /** Список + число товаров без вида (баннер «Проставить по категориям»). */
  list: async (params?: { includeArchived?: boolean }) => {
    const qs = params?.includeArchived ? "?includeArchived=true" : "";
    const res = await apiFetchWithMeta<ProductKind[], { productsWithoutKind: number }>(
      `${BASE}${qs}`,
    );
    return { items: res.data, productsWithoutKind: res.meta?.productsWithoutKind ?? 0 };
  },
  findOne: (id: string) => apiFetch<ProductKind>(`${BASE}/${id}`),
  create: (input: ProductKindInput) =>
    apiFetch<ProductKind>(BASE, { method: "POST", json: input }),
  update: (id: string, input: Partial<ProductKindInput>) =>
    apiFetch<ProductKind>(`${BASE}/${id}`, { method: "PATCH", json: input }),
  archive: (id: string) => apiFetch<ProductKind>(`${BASE}/${id}/archive`, { method: "POST" }),
  restore: (id: string) => apiFetch<ProductKind>(`${BASE}/${id}/restore`, { method: "POST" }),
  /** 409 `product_kind_in_use` — на вид ссылаются категории или товары. */
  remove: (id: string) =>
    apiFetch<{ deleted: true }>(`${BASE}/${id}`, { method: "DELETE" }),
  assignPreview: () => apiFetch<AssignPreviewResult>(`${BASE}/assign-preview`),
  assignByCategories: (assignments: Array<{ categoryId: string; productKindId: string }>) =>
    apiFetch<AssignResult>(`${BASE}/assign-by-categories`, {
      method: "POST",
      json: { assignments },
    }),
};
